import React, { useMemo, useState } from 'react';
import { Alert } from '@mui/material';
import {
    useContractDetail,
    useContractDetailData,
} from '../../../contextsProperty/ContextContractDetail';
import useTranslate from '../../../../customHooks/useTranslate';
import SectionTitle from '../../yourProperties/Title';
import useLanguage from '../../../../customHooks/useLanguage';
import Menu from '../../components/Menu';
import Card from '../../../../muiComponents/Card';
import Button from '../../../../muiComponents/Button';
import TimelineCard from './Timelinecard';
import {
    getEmptyTimelineSegment,
    orderSeasonsByStartDate,
    seasonColors,
} from '../../../../utils/contractUtils/contractUtils';
import {
    ContractDetailPanelNames,
    IContractTimelineSegment,
} from '../../../../types/contracts/contractTypes';
import { formatDate } from '../../../../utils/formatDates';
import '../../../../stylesheets/stylesComponents/timeline.scss';
import SeasonPanel from './panels/SeasonPanel';
import Label from '../../yourProperties/Label';
import DeletePopUp from './DeletePopUp';
import { useNotification } from '../../../../contexts/NotificationsContext';
import { FeedbackMessages } from '../../../../utils/commonVariables/messages';
import LoadingSection from './LoadingSection';
import useUserProfile from '../../../../customHooks/useUserProfile';

const Seasonality = () => {
    const translate = useTranslate();
    const language = useLanguage();
    const { readOnlyUser } = useUserProfile();

    const { setErrorMessage, setSuccessMessage } = useNotification();
    const {
        handleLoadingView,
        isMainInfoEmpty,
        isBlockedContract,
        handleOpenPanel,
        handleOpenSeasonPanel,
        openedPanel,
        handleSaveSeason,
        isLoadingView,
    } = useContractDetail();

    const { activeFrom, activeTo, seasons } = useContractDetailData();
    const [deletePopUpVisible, setDeletePopUpVisible] = useState('');

    const timelineSegments = useMemo<IContractTimelineSegment[]>(() => {
        if (!activeFrom || !activeTo) return [];

        const startDate = activeFrom as Date;
        const endDate = activeTo as Date;
        const orderedSeasons = orderSeasonsByStartDate(seasons);
        const segments: IContractTimelineSegment[] = [];
        let currentDate = new Date(startDate);

        orderedSeasons.forEach((season, index) => {
            const seasonStart = season.start as Date;
            const seasonEnd = season.end as Date;

            if (seasonStart > currentDate) {
                segments.push(
                    getEmptyTimelineSegment(currentDate, seasonStart)
                );
            }

            segments.push({
                ...season,
                start: seasonStart,
                end: seasonEnd,
                color: seasonColors[index],
            });

            currentDate = new Date(seasonEnd);
            currentDate.setDate(currentDate.getDate() + 1);
        });
        if (currentDate <= endDate) {
            segments.push(getEmptyTimelineSegment(currentDate, endDate));
        }

        return segments;
    }, [activeFrom, activeTo, seasons]);

    const isTimePeriodFull = useMemo(() => {
        return !timelineSegments.filter(seg => !seg.name).length;
    }, [timelineSegments]);

    const handleCloseSeasonPanel = () => {
        handleOpenPanel(null);
    };

    return (
        <>
            {deletePopUpVisible && !readOnlyUser && (
                <DeletePopUp
                    open={!!deletePopUpVisible}
                    onSave={async () => {
                        const deletedSeason = seasons.find(
                            season => season.id === deletePopUpVisible
                        );
                        if (!deletedSeason) return;
                        try {
                            handleLoadingView(true);
                            await handleSaveSeason({
                                ...deletedSeason,
                                Deleted: true,
                            });
                            setSuccessMessage(
                                FeedbackMessages.COMMON_SUCCESS_DELETE
                            );
                        } catch (error) {
                            setErrorMessage(FeedbackMessages.COMMON_ERROR);
                        } finally {
                            handleLoadingView(false);
                            setDeletePopUpVisible('');
                        }
                    }}
                    onCancel={() => {
                        setDeletePopUpVisible('');
                    }}
                ></DeletePopUp>
            )}
            {openedPanel === ContractDetailPanelNames.SEASONS && (
                <SeasonPanel
                    open={openedPanel === ContractDetailPanelNames.SEASONS}
                    onClose={handleCloseSeasonPanel}
                />
            )}
            <section className='contract-detail__section'>
                <div className='flex-between flex-align-center'>
                    <SectionTitle text={translate('seasonality')} />
                    <div className='flex-between flex-align-center gap-20'>
                        {!isMainInfoEmpty && (
                            <>
                                <Label
                                    translated={false}
                                    text={`${formatDate(activeFrom, language)}
                                 - 
                                ${formatDate(activeTo, language)}`}
                                />
                                <Menu
                                    menuItems={[
                                        {
                                            text: 'add',
                                            icon: 'Add',
                                            disabled:
                                                isTimePeriodFull ||
                                                isBlockedContract ||
                                                readOnlyUser,
                                            onClick: () => {
                                                handleOpenSeasonPanel({
                                                    start: activeFrom,
                                                    __isAdded: true,
                                                    __isChanged: true,
                                                });
                                            },
                                        },
                                    ]}
                                />
                            </>
                        )}
                    </div>
                </div>
                {isLoadingView ? (
                    <LoadingSection />
                ) : seasons.length ? (
                    <Card style={{ padding: '20px 30px', minHeight: '150px' }}>
                        <div className='timeline-container'>
                            {timelineSegments.map(segment => (
                                <TimelineCard
                                    key={segment.id}
                                    segment={segment}
                                    disabled={isBlockedContract || readOnlyUser}
                                    onDisable={async () => {
                                        const targetSeason = seasons.find(
                                            season => season.id === segment.id
                                        );
                                        if (!targetSeason) return;
                                        try {
                                            handleLoadingView(true);
                                            await handleSaveSeason({
                                                ...targetSeason,
                                                enabled: !targetSeason.enabled,
                                                __isChanged: true,
                                            });
                                            setSuccessMessage(
                                                FeedbackMessages.COMMON_SUCCESS_DELETE
                                            );
                                        } catch (error) {
                                            setErrorMessage(
                                                FeedbackMessages.COMMON_ERROR
                                            );
                                        } finally {
                                            handleLoadingView(false);
                                            setDeletePopUpVisible('');
                                        }
                                    }}
                                    onDelete={id => setDeletePopUpVisible(id)}
                                />
                            ))}
                        </div>
                    </Card>
                ) : (
                    <Card
                        className='contract-card__empty flex-center width100perc'
                        style={{ minHeight: '150px', padding: '10px 20px' }}
                    >
                        {isMainInfoEmpty ? (
                            <Alert severity='info' sx={{ width: '100%' }}>
                                {translate('add_main_info_first')}
                            </Alert>
                        ) : (
                            <Button
                                startIcon='Add'
                                disabled={
                                    isTimePeriodFull ||
                                    isBlockedContract ||
                                    readOnlyUser
                                }
                                onClick={() => {
                                    handleOpenSeasonPanel({
                                        start: activeFrom,
                                        __isAdded: true,
                                        __isChanged: true,
                                    });
                                }}
                            >
                                {translate('add')}
                            </Button>
                        )}
                    </Card>
                )}
            </section>
        </>
    );
};

export default Seasonality;
